<template>
  <div class="py-5 md:px-5">
    <PageHeader class="container" title="Products">
      <router-link :to="{ name: 'ProductCreate' }" class="btn btn-primary">
        Add Product
      </router-link>
    </PageHeader>

    <div class="container">
      <!-- List -->
      <div class="card">
        <!-- Header -->
        <div class="flex flex-col justify-between p-4 border-b md:flex-row lg:p-5">
          <div class="grid grid-cols-2 gap-3 md:flex">
            <!-- Search -->
            <fieldset class="relative col-span-2">
              <fa :icon="['far', 'search']" class="absolute top-0 left-0 m-2 text-gray-400 pointer-events-none" />
              <input type="text" class="py-1 pl-8 form-input" placeholder="Search..." v-model="filter.search" @keydown.enter="applyFilter">
            </fieldset>
            <!-- Sort -->
            <sort-dropdown :options="sortOptions" />
          </div>
        </div>

        <!-- Loading -->
        <Loading v-if="loading" class="p-6" />

        <!-- Empty -->
        <div v-else-if="products.meta.total < 1 && !$route.query.filter" class="max-w-xs py-12 mx-auto text-center">
          <div class="mb-2 text-lg font-bold">
            No products have been added yet.
          </div>
          <div class="text-gray-500">
            Create a product to add it as an option on your booking forms.
          </div>
          <router-link :to="{ name: 'ProductCreate' }" class="mt-4 btn btn-secondary">
            Create a Product
          </router-link>
        </div>

        <!-- No Results -->
        <div v-else-if="products.meta.total < 1 && $route.query.filter" class="max-w-xs py-12 mx-auto text-center">
          <div class="mb-2 text-lg font-bold">
            No products matching your filters.
          </div>
          <div class="text-gray-500">
            Try changing your filter settings, or clear the filter to see all products.
          </div>
          <button class="mt-4 btn btn-outline" @click="clearFilter">
            Clear Filters
          </button>
        </div>

        <!-- Results -->
        <div v-else class="divide-y">
          <!-- Table -->
          <div v-for="product in products.data" :key="product.id">
            <router-link :to="{ name: 'ProductEdit', params: { id: product.id } }" class="grid items-center grid-cols-2 gap-4 px-5 py-4 text-gray-800 lg:grid-cols-3 hover:no-underline hover:bg-gray-100">
              <div class="flex items-center space-x-4">
                <div class="flex items-center justify-center flex-none w-12 h-12 overflow-hidden border rounded bg-gray-50">
                  <img v-if="product.image" :src="product.image" :alt="product.name" class="object-cover h-full">
                  <fa v-else :icon="['far', 'cube']" class="text-lg text-gray-300" />
                </div>
                <div>
                  <div class="flex items-center font-semibold">
                    {{ product.name }} 
                  </div>
                  <div class="text-gray-500">
                    <currency :value="product.price" :currency-code="account.currency" :subunits-value="true" />
                  </div>
                </div>
              </div>
              <div class="text-right">
                <div v-if="product.status === 'active'">
                  <badge class="text-green-800 bg-green-100">Active</badge>
                </div>
                <div v-else-if="product.status === 'draft'">
                  <badge class="text-cyan-800 bg-cyan-100">Draft</badge>
                </div>
              </div>
            </router-link>
          </div>
          <!-- Pagination -->
          <Pagination class="rounded-b-lg" :total="products.meta.total" :per-page="products.meta.per_page" :current-page="products.meta.current_page" />
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import SortDropdown from '@/components/SortDropdown'

export default {
  name: 'ProductList',

  metaInfo: {
    title: 'Products'
  },

  components: {
    SortDropdown
  },

  created () {
    this.setFilter()
    this.getProducts()
  },

  computed: {
    ...mapGetters(['account'])
  },

  data () {
    return {
      submitting: false,
      loading: true,
      products: {},
      filter: {
        search: ''
      },
      sortOptions: [
        { label: `Created <span class="text-gray-500">(Newest First)</span>`, prop: 'created_at', dir: 'desc' },
        { label: `Created <span class="text-gray-500">(Oldest First)</span>`, prop: 'created_at', dir: 'asc' },
        { label: `Last Updated <span class="text-gray-500">(Newest First)</span>`, prop: 'updated_at', dir: 'desc' },
        { label: `Last Updated <span class="text-gray-500">(Oldest First)</span>`, prop: 'updated_at', dir: 'asc' },
        { label: `Name <span class="text-gray-500">(A&rarr;Z)</span>`, prop: 'name', dir: 'asc' },
        { label: `Name <span class="text-gray-500">(Z&rarr;A)</span>`, prop: 'name', dir: 'desc' }
      ]
    }
  },

  methods: {
    async getProducts () {
      try {
        this.loading = true
        const { data } = await this.$api.get(`account/${this.$route.params.account}/products`, {
          params: {
            page: this.$route.query.page || 1,
            sort: this.$route.query.sort || 'created_at',
            dir: this.$route.query.dir || 'desc',
            ...this.filter && { filter: this.filter }
          }
        })
        this.products = data.products
        this.loading = false
      } catch (error) {
        console.error(error)
        this.$toasted.error(error.response.data.message)
      }
    },

    setFilter () {
      if (this.$route.query.filter) {
        this.filter = JSON.parse(this.$route.query.filter)
      }
    },

    applyFilter () {
      this.$router.push({ 
        query: { 
          ...this.$route.query, 
          filter: JSON.stringify(this.filter)
        } 
      })
    },

    clearFilter () {
      this.filter = {
        search: '',
        orderdate: { start: sub(new Date(), { months: 1 }), end: add(new Date(), { months: 1 }) }
      }
      this.$router.push({ 
        query: { 
          ...this.$route.query, 
          filter: undefined
        } 
      })
    }
  },

  watch: {
    '$route.query': {
      handler (newVal, oldVal) {
        this.getProducts()
      },
      deep: true
    }
  }
}
</script>